/**
 * Shared organizer navigation definitions for Spotlight (and future sidebar reuse).
 * Paths mirror SideNavMenu, EventSideNavMenu, and UserEventSideNavMenu.
 */

export type OrganizerNavContext = 'common' | 'event' | 'venue-event';

export type OrganizerNavGroup = 'Navigation' | 'Quick actions';

export type OrganizerNavItem = {
  id: string;
  label: string;
  description?: string;
  /** Route path, or empty when the action is not a navigation (e.g. theme toggle) */
  path: string;
  group: OrganizerNavGroup;
  keywords?: string[];
  /** Named icon key — mapped to lucide icons in the spotlight hook */
  iconKey?: string;
  /** Non-navigation action key handled by the spotlight hook */
  actionKey?: 'theme' | 'logout' | 'create-event';
};

export type CommonNavFlags = {
  isEventSecondAdmin: boolean;
  isCoOrganizer: boolean;
  isPayoutEnabled: boolean;
};

export type EventNavFlags = {
  eventId: string | number;
  eventName: string;
  hasMultipleVenues: boolean;
  hasMultipleTicketGroups: boolean;
  hasAgendaData: boolean;
  hasSlotData: boolean;
  isEventClosed: boolean;
};

export type VenueEventNavFlags = {
  eventId: string | number;
  eventName: string;
  hasSlotData: boolean;
};

/** Detect which sidebar/spotlight context matches the current route. */
export function getOrganizerNavContext(pathname: string): OrganizerNavContext {
  if (pathname.includes('/venue-event/')) return 'venue-event';
  if (pathname.includes('/event/')) return 'event';
  return 'common';
}

/** Global organizer sidebar links (when no event is selected). */
export function getCommonNavItems(flags: CommonNavFlags): OrganizerNavItem[] {
  const { isEventSecondAdmin, isCoOrganizer, isPayoutEnabled } = flags;
  const items: OrganizerNavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      description: 'Organizer workspace home',
      path: '/home',
      group: 'Navigation',
      iconKey: 'LayoutDashboard',
      keywords: ['home', 'workspace'],
    },
    {
      id: 'events',
      label: 'Events',
      description: 'Browse and manage your events',
      path: '/events',
      group: 'Navigation',
      iconKey: 'Calendar',
      keywords: ['list', 'directory'],
    },
  ];

  if (!isEventSecondAdmin) {
    if (!isCoOrganizer) {
      items.push(
        {
          id: 'create-event',
          label: 'Create Event',
          description: 'Start a new event setup',
          path: '/create-quick-event',
          group: 'Navigation',
          iconKey: 'PlusCircle',
          keywords: ['new', 'add'],
        },
        {
          id: 'create-quick-event',
          label: 'Create Free Event',
          description: 'Fast event creation flow',
          path: '/create-quick-event?freeBooking=true',
          group: 'Navigation',
          iconKey: 'PlusCircle',
          keywords: ['quick', 'new', 'freebooking'],
        }
      );
    }

    items.push({
      id: 'contact-lists',
      label: 'Contact List',
      description: 'Manage organizer contacts',
      path: '/contact-lists',
      group: 'Navigation',
      iconKey: 'Users',
      keywords: ['contacts', 'people'],
    });

    if (!isCoOrganizer) {
      items.push({
        id: 'add-sub-user',
        label: 'Add Sub User',
        description: 'Invite a team member',
        path: '/add-new-sub-user',
        group: 'Navigation',
        iconKey: 'UserPlus',
        keywords: ['team', 'invite'],
      });
    }

    if (isPayoutEnabled) {
      items.push({
        id: 'payouts',
        label: 'Payouts',
        description: 'View payout settings and history',
        path: '/payouts',
        group: 'Navigation',
        iconKey: 'CreditCard',
        keywords: ['stripe', 'money'],
      });
    }

    items.push({
      id: 'promoters',
      label: 'Promoters',
      description: 'Add promoters and boosting coupon codes',
      path: '/promoters',
      group: 'Navigation',
      iconKey: 'Megaphone',
      keywords: ['boost', 'coupon', 'commission'],
    });

    items.push(
      {
        id: 'reports',
        label: 'Detailed Reports',
        description: 'Run organizer-level reports',
        path: '/reports/run-report',
        group: 'Navigation',
        iconKey: 'BarChart3',
        keywords: ['analytics', 'stats'],
      },
      {
        id: 'discover',
        label: 'Discover',
        description: 'Explore discovery tools',
        path: '/discover',
        group: 'Navigation',
        iconKey: 'Compass',
      },
      {
        id: 'ask-ev',
        label: 'Ask Ev',
        description: 'AI assistant for organizers',
        path: '/ai',
        group: 'Navigation',
        iconKey: 'Sparkles',
        keywords: ['ai', 'assistant', 'ev'],
      }
    );

    if (isPayoutEnabled) {
      items.push({
        id: 'payout-request',
        label: 'Payout Request',
        description: 'Request a payout',
        path: '/payout-request',
        group: 'Navigation',
        iconKey: 'Wallet',
        keywords: ['withdraw'],
      });
    }
  }

  return items;
}

/** Event-selected sidebar links (paths under /event/:eventId). */
export function getEventNavItems(flags: EventNavFlags): OrganizerNavItem[] {
  const {
    eventId,
    eventName,
    hasMultipleVenues,
    hasMultipleTicketGroups,
    hasAgendaData,
    hasSlotData,
    isEventClosed,
  } = flags;
  const base = `/event/${eventId}`;

  const items: OrganizerNavItem[] = [
    {
      id: 'event-dashboard',
      // Keep label as "Dashboard" so Spotlight search finds it (not only the event name)
      label: 'Dashboard',
      description: eventName ? `${eventName} — event overview` : 'Event overview and insights',
      path: base,
      group: 'Navigation',
      iconKey: 'LayoutDashboard',
      keywords: ['aura', 'home', 'overview', 'dashboard', eventName].filter(Boolean) as string[],
    },
    {
      id: 'edit-event',
      label: 'Edit Event',
      description: 'Update event details',
      path: `${base}/edit-event`,
      group: 'Navigation',
      iconKey: 'PenSquare',
      keywords: ['edit', 'settings'],
    },
  ];

  if (hasMultipleVenues) {
    items.push({
      id: 'venue',
      label: 'Venue',
      description: 'Manage event venues',
      path: `${base}/venue`,
      group: 'Navigation',
      iconKey: 'Building',
    });
  }

  if (hasMultipleTicketGroups) {
    items.push({
      id: 'ticket-group',
      label: 'Ticket Group',
      description: 'Manage ticket groups',
      path: `${base}/ticket-group`,
      group: 'Navigation',
      iconKey: 'Layers',
    });
  }

  items.push({
    id: 'tickets',
    label: 'Tickets',
    description: 'Manage ticket types and pricing',
    path: `${base}/tickets`,
    group: 'Navigation',
    iconKey: 'Ticket',
  });

  if (hasAgendaData) {
    items.push({
      id: 'agenda',
      label: 'Agenda',
      description: 'Event agenda schedule',
      path: `${base}/agenda`,
      group: 'Navigation',
      iconKey: 'CheckSquare',
    });
  }

  items.push(
    {
      id: 'sponsor',
      label: 'Sponsor',
      description: 'Manage sponsors',
      path: `${base}/sponsor`,
      group: 'Navigation',
      iconKey: 'Heart',
    },
    {
      id: 'coupons',
      label: 'Coupons',
      description: 'Discount codes and promotions',
      path: `${base}/coupons`,
      group: 'Navigation',
      iconKey: 'Percent',
      keywords: ['promo', 'discount'],
    },
    {
      id: 'lineup',
      label: 'Line Up',
      description: 'Artists and lineup',
      path: `${base}/lineup`,
      group: 'Navigation',
      iconKey: 'Mic',
      keywords: ['artists', 'performers'],
    },
    {
      id: 'sell-tickets',
      label: 'Sell Tickets',
      description: 'Manual ticket sales',
      path: `${base}/sell-ticket`,
      group: 'Navigation',
      iconKey: 'Coins',
      keywords: ['pos', 'sell'],
    },
    {
      id: 'event-reports',
      label: 'Reports',
      description: 'Event reports and analytics',
      path: `${base}/run-report`,
      group: 'Navigation',
      iconKey: 'BarChart3',
    }
  );

  if (isEventClosed) {
    items.push({
      id: 'payout-receipt',
      label: 'Payout Receipt',
      description: 'View payout receipt for closed event',
      path: `${base}/payout-receipt`,
      group: 'Navigation',
      iconKey: 'Receipt',
    });
  }

  items.push(
    {
      id: 'event-contacts',
      label: 'Contacts',
      description: 'Event contact list',
      path: `${base}/contacts`,
      group: 'Navigation',
      iconKey: 'UsersRound',
    },
    {
      id: 'forum',
      label: 'Forum',
      description: 'Event enquiry inbox',
      path: `${base}/forum`,
      group: 'Navigation',
      iconKey: 'MessageSquare',
    }
  );

  if (hasSlotData) {
    items.push({
      id: 'slot-bookings',
      label: 'Slot Bookings',
      description: 'Manage booking slots',
      path: `${base}/slot`,
      group: 'Navigation',
      iconKey: 'CalendarRange',
    });
  }

  return items;
}

/** Venue-event sidebar links (paths under /venue-event/:eventId). */
export function getVenueEventNavItems(flags: VenueEventNavFlags): OrganizerNavItem[] {
  const { eventId, eventName, hasSlotData } = flags;
  const base = `/venue-event/${eventId}`;

  const items: OrganizerNavItem[] = [
    {
      id: 'venue-event-dashboard',
      label: 'Dashboard',
      description: eventName ? `${eventName} — venue event overview` : 'Venue event overview',
      path: `${base}/`,
      group: 'Navigation',
      iconKey: 'LayoutDashboard',
      keywords: ['home', 'overview', 'dashboard', eventName].filter(Boolean) as string[],
    },
    {
      id: 'venue-event-tickets',
      label: 'Tickets',
      description: 'Manage tickets',
      path: `${base}/tickets`,
      group: 'Navigation',
      iconKey: 'Ticket',
    },
    {
      id: 'venue-event-coupons',
      label: 'Coupons',
      description: 'Promotions and coupons',
      path: `${base}/coupons`,
      group: 'Navigation',
      iconKey: 'Percent',
      keywords: ['promo', 'discount'],
    },
    {
      id: 'venue-event-reports',
      label: 'Reports',
      description: 'Venue event reports',
      path: `${base}/reports`,
      group: 'Navigation',
      iconKey: 'BarChart3',
    },
    {
      id: 'venue-event-contacts',
      label: 'Contact List',
      description: 'Venue event contacts',
      path: `${base}/contact-lists`,
      group: 'Navigation',
      iconKey: 'Users',
    },
  ];

  if (hasSlotData) {
    items.push({
      id: 'venue-event-slot',
      label: 'Slot Bookings',
      description: 'Manage booking slots',
      path: `${base}/slot`,
      group: 'Navigation',
      iconKey: 'CalendarRange',
    });
  }

  return items;
}

/**
 * Always-available workspace shortcuts (organizer home / events list).
 * Shown alongside event tools so you can leave an event from Spotlight.
 */
export function getWorkspaceNavItems(): OrganizerNavItem[] {
  return [
    {
      id: 'workspace-dashboard',
      label: 'Organizer Dashboard',
      description: 'Go to organizer workspace home',
      path: '/home',
      group: 'Navigation',
      iconKey: 'LayoutDashboard',
      keywords: ['home', 'workspace', 'dashboard', 'aura'],
    },
    {
      id: 'workspace-events',
      label: 'All Events',
      description: 'Browse your events directory',
      path: '/events',
      group: 'Navigation',
      iconKey: 'Calendar',
      keywords: ['list', 'directory', 'events'],
    },
  ];
}

/**
 * Quick actions available in every context.
 * Create Event is only offered when the user can create events.
 */
export function getQuickActionItems(options: {
  canCreateEvent: boolean;
  isNightMode: boolean;
}): OrganizerNavItem[] {
  const { canCreateEvent, isNightMode } = options;
  const items: OrganizerNavItem[] = [];

  if (canCreateEvent) {
    items.push({
      id: 'qa-create-event',
      label: 'Create Event',
      description: 'Open the create event flow',
      path: '/create-quick-event-v2',
      group: 'Quick actions',
      iconKey: 'PlusCircle',
      actionKey: 'create-event',
      keywords: ['new', 'add'],
    });
  }

  items.push(
    {
      id: 'qa-profile',
      label: 'My Profile',
      description: 'View and edit organizer profile',
      path: '/profile',
      group: 'Quick actions',
      iconKey: 'User',
      keywords: ['account', 'settings'],
    },
    {
      id: 'qa-theme',
      label: isNightMode ? 'Switch to Classic Gray' : 'Switch to Eventox Original',
      description: 'Toggle app theme',
      path: '',
      group: 'Quick actions',
      iconKey: 'Palette',
      actionKey: 'theme',
      keywords: ['dark', 'light', 'night', 'theme'],
    },
    {
      id: 'qa-logout',
      label: 'Logout',
      description: 'Sign out of organizer workspace',
      path: '/login',
      group: 'Quick actions',
      iconKey: 'LogOut',
      actionKey: 'logout',
      keywords: ['sign out', 'exit'],
    }
  );

  return items;
}
